import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Home as HomeIcon } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { apiRequest } from "../config/api";
import { useOrganization } from "../contexts/OrganizationContext";
import { getSafePlayerRedirect } from "../utils/redirectAfterLogin";
import splLogo from "../img/SPL_Logo.png";
import padelEagleLogo from "../img/padelEagle.png";
import PasswordTextField from "../components/PasswordTextField";

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [announcement, setAnnouncement] = useState<any>(null);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordUsername, setForgotPasswordUsername] = useState("");
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordRequiresManualReview, setForgotPasswordRequiresManualReview] = useState(false);
  const [forgotPasswordAlreadySent, setForgotPasswordAlreadySent] = useState(false);
  const [forgotPasswordCooldownExpiry, setForgotPasswordCooldownExpiry] = useState<Date | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const { unifiedLogin, user, playerUser } = useAuth();
  const { currentOrg } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine organization-specific settings
  const isPadelEagle = currentOrg?.slug === "padel-eagle";
  const PE_BLUE = isPadelEagle ? "#743444" : "#1976d2";
  const PE_ORANGE = isPadelEagle ? "#E49421" : "#42a5f5";
  // Priority: custom uploaded logo > hardcoded logo
  const orgLogo = currentOrg?.branding?.logo || (isPadelEagle ? padelEagleLogo : splLogo);
  const orgName = isPadelEagle ? "Padel Eagle" : "Stuttgart Padel League";
  const orgIcon = isPadelEagle ? "🦅" : "🎾";

  // Check if user was redirected due to token expiration
  const redirectPath = localStorage.getItem("redirectAfterLogin");
  const sessionExpired = !!redirectPath;
  const isPlayerLoginRoute = location.pathname.startsWith("/player");
  const isImpersonating = localStorage.getItem("impersonating") === "true";

  // Fetch announcement on mount
  React.useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const response = await apiRequest("/api/announcements/active/login");
        if (response.ok) {
          const data = await response.json();
          setAnnouncement(data?.message ? data : null);
        }
      } catch (error) {
        console.error("Error fetching announcement:", error);
      }
    };
    fetchAnnouncement();
  }, []);

  useEffect(() => {
    if (!forgotPasswordCooldownExpiry) return;
    const update = () => {
      const diffMs = forgotPasswordCooldownExpiry.getTime() - Date.now();
      if (diffMs <= 0) {
        setCooldownRemaining("now");
        return;
      }
      const mins = Math.floor(diffMs / 60000);
      const secs = Math.floor((diffMs % 60000) / 1000);
      setCooldownRemaining(`${mins}m ${secs}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [forgotPasswordCooldownExpiry]);

  // Redirect if already logged in
  if (playerUser && (isPlayerLoginRoute || isImpersonating)) {
    return <Navigate to="/player/dashboard" replace />;
  }

  if (user) {
    if (user.role === "admin") {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (user.role === "scorekeeper") {
      return <Navigate to="/" replace />;
    }
  }

  if (playerUser) {
    return <Navigate to="/player/league-days" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await unifiedLogin(username, password);
      if (result.success) {
        // The unifiedLogin will determine the role and set appropriate state
        // We need to wait a moment for state to update, then redirect
        setTimeout(() => {
          // Check if there's a redirect path saved (from token expiration)
          const redirectPath = localStorage.getItem("redirectAfterLogin");
          // Check what type of account logged in
          const savedAuthUser = localStorage.getItem("authUser");
          const savedPlayerUser = localStorage.getItem("playerAuthUser");

          if (redirectPath) {
            localStorage.removeItem("redirectAfterLogin");
            // Players only follow a saved redirect if it is a page they can open
            navigate(
              savedAuthUser ? redirectPath : getSafePlayerRedirect(redirectPath),
            );
            return;
          }

          if (savedPlayerUser && (isPlayerLoginRoute || isImpersonating)) {
            navigate("/player/dashboard");
          } else if (savedAuthUser) {
            const authUser = JSON.parse(savedAuthUser);
            if (authUser.role === "admin") {
              navigate("/admin/dashboard");
            } else {
              navigate("/");
            }
          } else if (savedPlayerUser) {
            navigate("/player/dashboard");
          }
        }, 100);
      } else {
        const message = result.error || "Invalid username or password";
        // Usernames are stored lowercase, so a capitalised attempt is the likely culprit
        const hasCapitals = username !== username.toLowerCase();
        setError(
          hasCapitals && /invalid username or password/i.test(message)
            ? `${message}. Try using lowercase for your username, no capital letters.`
            : message,
        );
      }
    } catch (err) {
      setError("Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setForgotPasswordError("");

    if (!forgotPasswordUsername) {
      setForgotPasswordError("Please enter your username or email");
      return;
    }

    setForgotPasswordLoading(true);

    try {
      const response = await apiRequest("/api/player/forgot-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          usernameOrEmail: forgotPasswordUsername,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setForgotPasswordSuccess(true);
        setForgotPasswordRequiresManualReview(data.requiresManualReview || false);
        setForgotPasswordAlreadySent(data.alreadySent || false);
        if (data.cooldownExpiresAt) {
          setForgotPasswordCooldownExpiry(new Date(data.cooldownExpiresAt));
        }
        setTimeout(() => {
          setForgotPasswordOpen(false);
          setForgotPasswordSuccess(false);
          setForgotPasswordRequiresManualReview(false);
          setForgotPasswordAlreadySent(false);
          setForgotPasswordCooldownExpiry(null);
          setCooldownRemaining("");
          setForgotPasswordUsername("");
          setForgotPasswordError("");
        }, 5000);
      } else {
        const data = await response.json();
        setForgotPasswordError(
          data.error || "Failed to submit password reset request",
        );
      }
    } catch (error) {
      console.error("Error requesting password reset:", error);
      setForgotPasswordError("Failed to submit password reset request");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: isPadelEagle
          ? "#743444"
          : "linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #81c784 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        py: 4,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: isPadelEagle ? 0 : 0.3,
        },
      }}
    >
      {isPadelEagle && (
        <>
          <Box
            component="video"
            src="https://video.wixstatic.com/video/615801_2444b967f440421ebb67dcbd3000ee08/480p/mp4/file.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center 38%",
              opacity: 0.5,
              mixBlendMode: "luminosity",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(180deg, rgba(60,18,30,0.35) 0%, rgba(60,18,30,0.15) 50%, rgba(60,18,30,0.5) 100%)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        </>
      )}
      <Container
        component="main"
        maxWidth="sm"
        sx={{ position: "relative", zIndex: 1 }}
      >
        {/* Announcement Marquee */}
        {announcement && (
          <Box
            sx={{
              bgcolor: announcement?.backgroundColor || "#d32f2f",
              color: "white",
              py: 1.5,
              mb: 3,
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
              boxShadow: 3,
            }}
          >
            <Box
              sx={{
                display: "inline-block",
                whiteSpace: "nowrap",
                animation: "marquee 20s linear infinite",
                paddingLeft: "100%",
                "@keyframes marquee": {
                  "0%": { transform: "translateX(0)" },
                  "100%": { transform: "translateX(-100%)" },
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" },
                  display: "inline-block",
                }}
              >
                📢 {announcement.message}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Home Button */}
        <Box
          sx={{
            position: "absolute",
            top: { xs: -60, md: -80 },
            left: 0,
            zIndex: 2,
          }}
        >
          <IconButton
            onClick={() => navigate("/")}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.3)",
                border: "2px solid rgba(255, 255, 255, 0.5)",
                transform: "scale(1.05)",
              },
              transition: "all 0.3s ease",
              backdropFilter: "blur(10px)",
            }}
            title="Back to Home"
          >
            <HomeIcon />
          </IconButton>
        </Box>

        <Paper
          elevation={24}
          sx={{
            padding: isPadelEagle ? { xs: 3, md: 3.5 } : { xs: 3, md: 5 },
            width: "100%",
            borderRadius: isPadelEagle ? "16px" : 4,
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            boxShadow:
              "0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Header with Logo and Icon */}
            <Box
              sx={{
                mb: isPadelEagle ? 2 : 3,
                p: isPadelEagle ? 1.5 : 2,
                borderRadius: isPadelEagle ? "16px" : 3,
                background: isPadelEagle
                  ? "#743444"
                  : (isPadelEagle ? "#743444" : "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)"),
                color: "white",
                textAlign: "center",
                boxShadow: isPadelEagle
                  ? "none"
                  : "0 8px 20px rgba(25, 118, 210, 0.3)",
              }}
            >
              {isPadelEagle && (
                <Box
                  component="img"
                  src={orgLogo}
                  alt="Padel Eagle"
                  sx={{
                    width: { xs: 56, md: 56 },
                    height: { xs: 56, md: 56 },
                    mb: 1,
                    // The logo is bordeaux/orange, so it sits on a white tile to stay visible on the bordeaux header.
                    p: 1,
                    borderRadius: 3,
                    bgcolor: "#fff",
                    objectFit: "contain",
                    boxSizing: "content-box",
                  }}
                />
              )}
              <Typography
                component="h1"
                variant="h4"
                sx={{
                  // The Padel Eagle theme paints every heading bordeaux, which vanishes on the bordeaux header.
                  color: "common.white",
                  fontWeight: "bold",
                  textShadow: isPadelEagle ? "none" : "1px 1px 2px rgba(0,0,0,0.2)",
                  fontSize: { xs: "1.75rem", md: "2.125rem" },
                }}
              >
                {orgIcon} {isPadelEagle ? "Padel Eagle" : "SPL"} Login
              </Typography>
            </Box>

            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{
                mb: 3,
                fontSize: { xs: "0.9rem", md: "1rem" },
                fontWeight: 500,
              }}
            >
              {orgName} 🏆
            </Typography>

            {sessionExpired && (
              <Alert severity="warning" sx={{ width: "100%", mb: 2 }}>
                Your session has expired. Please login again to continue.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ mt: 1, width: "100%" }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: (isPadelEagle ? "#743444" : PE_BLUE),
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: (isPadelEagle ? "#743444" : PE_BLUE),
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: (isPadelEagle ? "#743444" : PE_BLUE),
                  },
                }}
              />
              <PasswordTextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: (isPadelEagle ? "#743444" : PE_BLUE),
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: (isPadelEagle ? "#743444" : PE_BLUE),
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: (isPadelEagle ? "#743444" : PE_BLUE),
                  },
                }}
              />

              {/* Forgot Password Link */}
              <Box sx={{ textAlign: "center", mt: 1, mb: 2 }}>
                <Button
                  variant="text"
                  onClick={() => setForgotPasswordOpen(true)}
                  sx={{ textTransform: "none", color: (isPadelEagle ? "#743444" : PE_BLUE) }}
                >
                  Forgot password?
                </Button>
              </Box>

              {/* Login Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 1,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 3,
                  background: isPadelEagle
                    ? "#E49421"
                    : (isPadelEagle ? "#743444" : "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)"),
                  boxShadow: isPadelEagle ? "none" : "0 6px 20px rgba(25, 118, 210, 0.3)",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  textTransform: "none",
                  ...(isPadelEagle && { borderRadius: 999 }),
                  "&:hover": {
                    background: isPadelEagle
                      ? "#74586C"
                      : (isPadelEagle ? "#743444" : "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)"),
                    boxShadow: isPadelEagle ? "none" : "0 8px 25px rgba(25, 118, 210, 0.4)",
                    transform: isPadelEagle ? "none" : "translateY(-1px)",
                  },
                  "&:disabled": {
                    background: isPadelEagle ? "#D8C4C1" : "linear-gradient(135deg, #ccc 0%, #999 100%)",
                    color: isPadelEagle ? "#fff" : undefined,
                    boxShadow: "none",
                  },
                  transition: "all 0.3s ease",
                }}
                disabled={isLoading || !username || !password}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {isLoading ? "Signing In..." : "🔐 Sign In"}
              </Button>

              {/* New players of tournament orgs create their account here */}
              {currentOrg?.competitionType === "tournament" && (
                <Box sx={{ textAlign: "center", mt: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                    New player?
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate("/player-registration")}
                    sx={{
                      py: 1.5,
                      borderRadius: 3,
                      borderColor: isPadelEagle ? "#E49421" : PE_BLUE,
                      color: isPadelEagle ? "#743444" : PE_BLUE,
                      fontSize: "1rem",
                      fontWeight: "bold",
                      textTransform: "none",
                      borderWidth: 2,
                      ...(isPadelEagle && { borderRadius: 999 }),
                    }}
                  >
                    Create your account here
                  </Button>
                </Box>
              )}

              {/* Home Button */}
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate("/")}
                startIcon={<HomeIcon />}
                sx={{
                  mt: 1,
                  py: 1.5,
                  borderRadius: 3,
                  borderColor: isPadelEagle ? "#743444" : PE_BLUE,
                  color: isPadelEagle ? "#743444" : PE_BLUE,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  textTransform: "none",
                  borderWidth: 2,
                  ...(isPadelEagle && { borderRadius: 999 }),
                  "&:hover": {
                    borderColor: isPadelEagle ? "#74586C" : PE_BLUE,
                    backgroundColor: isPadelEagle ? "#74586C" : "rgba(25, 118, 210, 0.04)",
                    color: isPadelEagle ? "#fff" : undefined,
                    borderWidth: 2,
                    transform: isPadelEagle ? "none" : "translateY(-1px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Back to Home
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={() => !forgotPasswordLoading && setForgotPasswordOpen(false)}
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {forgotPasswordSuccess ? (
            <Alert severity={forgotPasswordAlreadySent ? "warning" : "success"} sx={{ mt: 2 }}>
              {forgotPasswordAlreadySent ? (
                <>
                  A reset email was already sent recently. Please check your inbox and spam folder.
                  {cooldownRemaining && cooldownRemaining !== "now" && (
                    <> You can request a new one in <strong>{cooldownRemaining}</strong>.</>
                  )}
                  {cooldownRemaining === "now" && (
                    <> You can now request a new reset email.</>
                  )}
                </>
              ) : forgotPasswordRequiresManualReview ? (
                <>
                  Your password reset request has been submitted to our admins. 
                  They will contact you shortly to help you reset your password.
                </>
              ) : (
                <>
                  Password reset request submitted! If your account exists, you'll receive an email with a reset link shortly.
                </>
              )}
            </Alert>
          ) : (
            <>
              {forgotPasswordError && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                  {forgotPasswordError}
                </Alert>
              )}
              <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
                Enter your username or email address to receive a password reset link.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label="Username or Email"
                placeholder="e.g., johndoe or john@example.com"
                value={forgotPasswordUsername}
                onChange={(e) => setForgotPasswordUsername(e.target.value)}
                disabled={forgotPasswordLoading}
                sx={{ mt: 1 }}
                helperText="Enter the username or email associated with your account"
              />
              <Typography
                variant="body2"
                sx={{
                  mt: 3,
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  fontStyle: "italic",
                }}
              >
                If you don't know your username or email, please contact an admin for assistance.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!forgotPasswordSuccess && (
            <>
              <Button
                onClick={() => setForgotPasswordOpen(false)}
                disabled={forgotPasswordLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleForgotPassword}
                variant="contained"
                disabled={
                  forgotPasswordLoading ||
                  !forgotPasswordUsername
                }
              >
                {forgotPasswordLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  "Submit Request"
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Login;
